import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ContextProvider } from '@lit/context';
import { WithUserProfile } from './mixins/with-user-profile';
import { WithActiveShop } from './mixins/with-active-shop';
import { WithRealtime } from './mixins/with-realtime';
import { ProductAvailabilityStore } from './state/product-availability-store';
import { ProductAvailabilityContext } from './contexts/product-availability-context';
import {
  evaluateDeviceBinding,
  getDeviceMode,
  getOrCreateDeviceId,
  setDeviceMode,
  type Mode,
} from './datatypes/device';
import { ApiFactory } from './api/easypos-api';
import { resolveRouterPath, router } from './router';
import {
  getUserContextMode,
  clearUserContextMode,
} from './state/user-context-mode';
import { getPendingJoinCode, clearPendingJoinCode } from './state/pending-join';

// LocalStorage key for the rejection flash that the login page reads
// once and clears, so the user sees "this device is for {shop}" right
// after we logged them out.
const DEVICE_BINDING_REJECTION_KEY = 'device-binding-rejection';

import './pages/app-home';
import './components/app-shell/app-shell';
import './components/register-lock-screen';
import { posDb } from './database/database';
import { themeStore } from './theme/theme-store';

// Apply persisted theme as early as possible so first paint matches
// the operator's saved choice (avoids a light→dark flash on dark-set
// devices). themeStore.init() reads localStorage + the matchMedia for
// auto mode, applies sl-theme-dark to <html> if needed.
themeStore.init();
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import './styles/global.css';
import { styles as sharedStyles } from './styles/shared-styles';

import { FacebookAuth } from './authentication/facebook-auth';
import {
  applyUpdate,
  hardResetAndReload,
  registerServiceWorker,
} from './sw-register';
import './components/install-prompt-card.js';
import { UserProfile } from './datatypes/user-profile';
import { IShopAccess, ILoginAccessToken } from './api/model/login-access-token';
import { IShopAccountRegister } from './api/model/requests';

registerServiceWorker();

@customElement('app-index')
export class AppIndex extends WithRealtime(
  WithActiveShop(WithUserProfile(LitElement))
) {
  get isLoggedIn() {
    return this.userProfile?.isLoggedIn ?? false;
  }

  @state() private _pendingUpdate: ServiceWorkerRegistration | null = null;

  // Independent of the SW lifecycle — set when the boot-time
  // version check sees the cached client version trailing the
  // live server version. Catches the case where the SW was unable
  // to update itself (e.g. it cached a broken precache manifest, or
  // the device launched offline against stale cache). In those
  // states, the SW won't emit `app-update-available`; we surface
  // the skew here as a separate signal so the operator can recover
  // with a hard reload that unregisters the SW entirely.
  @state() private _versionSkewServer: string | null = null;

  // Resolved per-(shop, device) mode — drives which nav sections the shell
  // shows. Set in _applyDeviceContext; defaults to all-in-one (full IA)
  // until device-context resolves.
  @state() private _deviceMode: Mode = 'all-in-one';

  // Register lock (manual). When true, the whole app is replaced by the
  // full-viewport <register-lock-screen>; the token has been cleared so nothing
  // can act as the walked-away cashier until a PIN unlocks. Survives reload via
  // a 'register-lock' record so a locked register reopens locked, not on /login.
  @state() private _locked = false;
  private _lockedContext: { cashierName: string; shopLabel: string } = {
    cashierName: '',
    shopLabel: '',
  };

  // Per-shop "currently sold out" overlay. Owned here so a single store
  // serves the whole app and reattaches when the active shop changes.
  private readonly _availability = new ProductAvailabilityStore();

  // Stable device id (UUID v4) for this physical tablet. Loaded once on
  // first render and used for the realtime negotiate body so the server
  // can touch the DeviceRegistration freshness signal.
  private _deviceId?: string;

  constructor() {
    super();
    // Provide the menu-availability store on the Lit context so any
    // component (sales-new-page, product-edit-page, etc.) can @consume it.
    // The ContextProvider attaches itself to `this` via the constructor
    // call; we retain the reference only for tree-shaker friendliness.
    new ContextProvider(this, {
      context: ProductAvailabilityContext,
      initialValue: this._availability,
    });
  }

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
      }

      /* Bare mode (login / shop-select / shop-create) uses this <main>
         directly — full-bleed, no chrome. The shelled path renders
         inside <app-shell>'s own <main> in its shadow DOM. */
      main {
        padding: 1rem clamp(0.75rem, 2vw, 1.5rem);
        max-width: 1280px;
        margin-left: auto;
        margin-right: auto;
      }

      .update-banner {
        position: fixed;
        left: 12px;
        right: 12px;
        bottom: 12px;
        z-index: 1000;
      }
    `,
  ];

  private async handleLogout() {
    // Tear down the realtime connection before clearing the profile so the
    // server-side group cleanup runs while we still have an auth token.
    await this._availability.attachToShop(undefined, undefined);
    await this.disconnectRealtime();
    await this.clearUserProfile();
    clearUserContextMode();
    clearPendingJoinCode();
    // A subsequent login on a fresh user should be allowed to auto-create
    // again. Reset the one-shot guard so logout → re-login → onboard works.
    this._autoCreateAttempted = false;
  }

  // Manual register lock: a cashier stepping away locks their identity. Tears
  // down realtime while the token is still valid, de-authorises WITHOUT routing
  // to /login (clearAuthForLock), persists a lock marker so a reload stays
  // locked, and flips to the lock screen. Unlock happens on the lock screen via
  // auth/pin-unlock, which dispatches user-profile-set (handled below).
  private async lockRegister() {
    if (!this.isLoggedIn || this._locked) return;
    this._lockedContext = {
      cashierName: this.userProfile?.displayName ?? '',
      shopLabel: this.activeShop?.shopName ?? '',
    };
    await this._availability.attachToShop(undefined, undefined);
    await this.disconnectRealtime();
    await this.clearAuthForLock();
    await posDb.master.setRecord('register-lock', this._lockedContext);
    this._locked = true;
  }

  private _onLockRegister = () => {
    void this.lockRegister();
  };

  // Password fallback from the lock screen (forgot PIN / no-PIN shop): drop the
  // lock and send the operator to the normal email + password login.
  private _onCancelLock = () => {
    void (async () => {
      await posDb.master.deleteRecord('register-lock');
      this._locked = false;
      void router.navigate(resolveRouterPath('login'));
    })();
  };

  // Onboarding helper: creates a sample shop for the current user without
  // making them fill in a form. Picks a slug derived from their display
  // name + a short random suffix so it's globally unique. On success the
  // existing shop-created event flow refreshes the user-profile and
  // routes to the dashboard. On failure the flag is set so the route
  // handler falls through to manual /shop/create.
  private async _autoCreateSampleShop() {
    if (this._autoCreatingSample || !this.userProfile) return;
    this._autoCreatingSample = true;
    this._autoCreateAttempted = true;
    try {
      const baseSlug = slugifyShopName(this.userProfile.displayName) || 'my';
      const suffix = Math.random().toString(36).slice(2, 8);
      const shopName = `${baseSlug}-sample-${suffix}`.slice(0, 32);
      const displayName = this.userProfile.displayName
        ? `${this.userProfile.displayName}'s sample shop`
        : 'My sample shop';
      const r = await ApiFactory.authenticated().createSampleShop({
        shopName,
        displayName,
        timezone: browserTimezone(),
      });
      if (!r.success || !r.data) {
        // Log it; the route handler will fall through to /shop/create
        // where the user can do it manually. _autoCreateAttempted is
        // already set so we don't retry-loop.
        console.warn('[onboarding] auto-create sample shop failed', r.error);
        return;
      }
      // Same flow as the manual sample page: refresh profile + select
      // the new shop + route to home.
      window.dispatchEvent(
        new CustomEvent('shop-created', {
          detail: { token: r.data.token, shop: r.data.shop },
        })
      );
      void router.navigate(resolveRouterPath(''));
    } finally {
      this._autoCreatingSample = false;
    }
  }

  // Loads the stable device id (cached on the instance after first load),
  // resolves the per-(shop, device) selected mode, and pushes both into
  // the realtime connection so the next negotiate carries them in the
  // request body. Safe to call before `connectRealtime` — the
  // RealtimeConnection holds the values until it negotiates.
  //
  // No-op when realtime hasn't been brought up yet (the values get set
  // again right before connect). Idempotent.
  private async _applyDeviceContext(shopName?: string) {
    if (!this._deviceId) {
      this._deviceId = await getOrCreateDeviceId();
    }
    const mode = shopName
      ? await getDeviceMode(shopName, this._deviceId)
      : 'all-in-one';
    this._deviceMode = mode;
    this.realtime?.setDeviceContext({
      deviceId: this._deviceId,
      mode,
      shopName,
    });
  }

  // Device-binding gate. Returns true when the user is allowed to proceed,
  // false when they were rejected (in which case this method has already
  // logged them out + queued a flash for the login page).
  //
  // When the device is bound to a shop the user IS a member of, this
  // method ALSO:
  //   - Sets the active shop to the bound shop (overriding whatever the
  //     local restoreActiveShopFor would have picked).
  //   - Persists the device's defaultMode for this (shop, device) pair so
  //     subsequent _applyDeviceContext picks it up on the next negotiate.
  //
  // Personal devices fall through with action='allow' and no overrides;
  // the existing restoreActiveShopFor / shop-picker flow runs normally.
  private async _runDeviceBindingGate(): Promise<boolean> {
    if (!this._deviceId) {
      this._deviceId = await getOrCreateDeviceId();
    }
    const r = await ApiFactory.authenticated().getDeviceBinding(this._deviceId);
    if (!r.success || !r.data) {
      // Network / server error during the gate. Don't lock anyone out on
      // a transient failure — fall through to personal-style behaviour.
      // Server-side enforcement (Phase O) will be the hard gate; the PWA
      // gate is a UX gate.
      return true;
    }
    const outcome = evaluateDeviceBinding(this.userProfile, r.data);
    if (outcome.action === 'reject') {
      try {
        localStorage.setItem(DEVICE_BINDING_REJECTION_KEY, outcome.reason);
      } catch {
        // localStorage unavailable (private mode etc.) — flash is best-
        // effort; the user sees a generic login page either way.
      }
      // Disconnect realtime + clear profile (also revokes the bearer
      // token server-side). Routes to /login automatically via the
      // user-profile setter.
      await this.disconnectRealtime();
      await this.clearUserProfile();
      return false;
    }
    if (outcome.autoSelectShop) {
      // Override restoreActiveShopFor: pick the bound shop directly.
      const bound = this.userProfile?.shopAccess.find(
        (s) => s.active && s.shopName.toLowerCase() === outcome.autoSelectShop
      );
      if (bound) {
        await this.setActiveShop(bound);
        if (outcome.forcedMode) {
          await setDeviceMode(
            bound.shopName,
            this._deviceId,
            outcome.forcedMode
          );
        }
      }
    }
    return true;
  }

  // Tracks the "user is currently interacting" state — bumped on
  // every keydown / pointer / wheel event, reset by the idle timer.
  // Used by the auto-reload-on-idle path to decide whether it's safe
  // to silently apply a waiting SW update without yanking the page
  // out from under an active cashier.
  private _idleTimer?: number;
  private _idleHandlers?: () => void;

  // 60s of no user input before we'll auto-apply a waiting update.
  // Short enough to feel responsive ("they put the device down → next
  // time they pick it up it's already updated"), long enough that a
  // pause mid-sale doesn't trigger a reload.
  private static readonly AUTO_RELOAD_IDLE_MS = 60 * 1000;

  private _onUpdateAvailable = (e: Event) => {
    this._pendingUpdate = (e as CustomEvent<ServiceWorkerRegistration>).detail;
    // Once we know an update is waiting, start watching for user
    // idleness. If the user goes 60s without input we silently apply
    // the update; otherwise the banner stays available for manual
    // tap. Either path arrives at the same "reload page on new SW
    // controllerchange" outcome.
    this._startIdleWatchForAutoReload();
  };

  private _startIdleWatchForAutoReload() {
    if (this._idleHandlers) return; // already watching
    const reset = () => {
      if (this._idleTimer !== undefined) window.clearTimeout(this._idleTimer);
      this._idleTimer = window.setTimeout(() => {
        if (this._pendingUpdate) this._reloadForUpdate();
      }, AppIndex.AUTO_RELOAD_IDLE_MS);
    };
    const events: (keyof WindowEventMap)[] = [
      'keydown',
      'pointerdown',
      'pointermove',
      'wheel',
      'touchstart',
    ];
    for (const ev of events)
      window.addEventListener(ev, reset, { passive: true });
    this._idleHandlers = () => {
      for (const ev of events) window.removeEventListener(ev, reset);
      if (this._idleTimer !== undefined) window.clearTimeout(this._idleTimer);
      this._idleTimer = undefined;
    };
    reset(); // start the first window immediately
  }

  private _onUserProfileSet = (e: Event): void => {
    void this._handleUserProfileSet(e);
  };

  private _handleUserProfileSet = async (e: Event) => {
    const profile = (e as CustomEvent<UserProfile>).detail;
    if (!profile) return;
    // Unlocking a locked register dispatches this same event. Drop the lock
    // marker + flag so the shell renders again instead of the lock screen.
    if (this._locked) {
      this._locked = false;
      await posDb.master.deleteRecord('register-lock');
    }
    await this.setUserProfile(profile);
    // Device-binding gate runs BEFORE the standard restoreActiveShopFor
    // flow so a shop-owned device can override the per-user shop choice
    // (or reject the user entirely if they're not a member of the bound
    // shop). When _runDeviceBindingGate returns false, it has already
    // cleared the user profile and queued a flash for the login page.
    const allowed = await this._runDeviceBindingGate();
    if (!allowed) return;
    if (!this.activeShop) {
      // Either the device is personal, or the binding gate didn't auto-
      // select (membership but no override). Standard flow picks up.
      await this.restoreActiveShopFor(profile);
    }
    this._routeForShopState();
    await this.connectRealtime();
    await this._applyDeviceContext(this.activeShop?.shopName);
    if (this.activeShop) {
      await this.realtimeJoinShop(this.activeShop.shopName);
      await this._availability.attachToShop(
        this.activeShop.shopName,
        this.realtime
      );
    }
    await this._attachPendingSalesStore();
  };

  // Q.3 — keep the pending-sales store pointed at the active shop so
  // the connection banner's "N pending" pill stays accurate and the
  // outbox drainer fires against the right API client + shop on
  // reconnect.
  private async _attachPendingSalesStore(): Promise<void> {
    const { pendingSalesStore } = await import('./state/pending-sales-store');
    const { ApiFactory } = await import('./api/easypos-api');
    pendingSalesStore.attachShop(
      this.activeShop?.shopName ?? null,
      this.activeShop ? ApiFactory.authenticated() : null
    );
  }

  // Fired by the shop-create page after a successful POST /api/shop. The
  // server returned a renewed token (with the new shop in ShopAccess) and
  // the new ShopAccountRegister; we update the user profile and pick the
  // freshly-created shop as active so the user lands inside it.
  private _onShopCreated = (e: Event): void => {
    void this._handleShopCreated(e);
  };

  private _handleShopCreated = async (e: Event) => {
    const detail = (
      e as CustomEvent<{
        token: ILoginAccessToken;
        shop: IShopAccountRegister;
      }>
    ).detail;
    if (!detail || !this.userProfile) return;

    const updatedProfile = new UserProfile({
      ...this.userProfile.profile,
      accessToken: detail.token,
    });
    await this.setUserProfile(updatedProfile);

    const newAccess = updatedProfile.shopAccess.find(
      (s) => s.shopName.toLowerCase() === detail.shop.shopName.toLowerCase()
    );
    if (newAccess) {
      await this.setActiveShop(newAccess);
      await this._applyDeviceContext(newAccess.shopName);
      await this.realtimeJoinShop(newAccess.shopName);
      await this._availability.attachToShop(newAccess.shopName, this.realtime);
      await this._attachPendingSalesStore();
    }

    // The userProfile setter no longer auto-navigates on set, so the user
    // would otherwise linger on /shop/create. Bounce to home explicitly now
    // that the new shop is active.
    void router.navigate(resolveRouterPath(''));
  };

  private _onShopSelected = (e: Event): void => {
    void this._handleShopSelected(e);
  };

  private _handleShopSelected = async (e: Event) => {
    const shop = (e as CustomEvent<IShopAccess>).detail;
    if (shop) {
      await this.setActiveShop(shop);
      await this._applyDeviceContext(shop.shopName);
      await this.realtimeJoinShop(shop.shopName);
      await this._availability.attachToShop(shop.shopName, this.realtime);
      await this._attachPendingSalesStore();
    }
  };

  private _reloadForUpdate() {
    if (this._pendingUpdate) applyUpdate(this._pendingUpdate);
  }

  // Boot-time version-skew probe. Fires once on connect, compares
  // the baked __APP_VERSION__ to whatever /api/version reports. If
  // different, the local cached client is stale AND the SW failed
  // to refresh it — render the recovery banner so the operator can
  // hard-reset out of the stuck state.
  //
  // Anonymous, GET, ~30 bytes. Negligible cost; runs once per app
  // load. Failures (offline, server unreachable) are silently
  // ignored — when the server isn't reachable we have no truth to
  // compare against, so we don't show the banner.
  private async _checkVersionSkew() {
    try {
      const r = await ApiFactory.anonymous().getServerVersion();
      if (!r.success || !r.data) return;
      // `getServerVersion` strips the +sha suffix; we compare the
      // semver part only. `__APP_VERSION__` from VERSION at build
      // time is the same shape (e.g. "0.1.5").
      //
      // Only surface the banner when the server is AHEAD of this
      // client — meaning the cached client is genuinely stale and the
      // SW didn't pick up the new code. When the client is AHEAD of
      // the server (e.g. an in-progress staggered deploy where the
      // frontend tag landed but the backend is still rolling), there
      // is nothing the user can do to "fix" it from the PWA — a hard
      // reset would just reload the same client they already have,
      // and the staleness will resolve when the backend deploy
      // finishes. Nagging the user in that window is noise.
      if (r.data && this._isServerNewer(r.data, __APP_VERSION__)) {
        this._versionSkewServer = r.data;
      }
    } catch {
      /* silently ignore — no truth available */
    }
  }

  // Semver-aware comparison. Returns true when `server` is strictly
  // newer than `client`. Treats malformed versions as "not newer" so
  // a parse failure doesn't ever pop the banner.
  private _isServerNewer(server: string, client: string): boolean {
    const parse = (v: string): [number, number, number] | null => {
      const m = v.match(/^(\d+)\.(\d+)\.(\d+)$/);
      if (!m) return null;
      return [Number(m[1]), Number(m[2]), Number(m[3])];
    };
    const s = parse(server);
    const c = parse(client);
    if (!s || !c) return false;
    for (let i = 0; i < 3; i++) {
      if (s[i] > c[i]) return true;
      if (s[i] < c[i]) return false;
    }
    return false;
  }

  private _hardResetForSkew() {
    void hardResetAndReload();
  }

  // Q.1 — disposer for the navigator online/offline listeners. Set by
  // connectedCallback so disconnectedCallback can tear down cleanly.
  private _navigatorDispose?: () => void;

  // Fires when the api client sees a 401 on a request that DID carry a
  // bearer token. Means the server doesn't recognise the token anymore
  // (expired / revoked / data wiped under us in dev). Tear down the
  // session and bounce to login.
  private _onAuthExpired = () => {
    if (!this.isLoggedIn) return;
    void this.handleLogout();
  };

  private _onLogoutRequested = () => {
    void this.handleLogout();
  };

  // Onboarding: a logged-in user with zero shops gets a sample shop
  // automatically rather than the bare "Create your shop" form. Both
  // the splash flag (renders the centered "setting up…" state) and the
  // re-entry guard (prevents _routeForShopState from kicking off a
  // second create while one is in flight) live here.
  @state() private _autoCreatingSample = false;
  private _autoCreateAttempted = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('app-update-available', this._onUpdateAvailable);
    window.addEventListener('user-profile-set', this._onUserProfileSet);
    window.addEventListener('shop-created', this._onShopCreated);
    window.addEventListener('shop-selected', this._onShopSelected);
    window.addEventListener('auth-expired', this._onAuthExpired);
    window.addEventListener('logout-requested', this._onLogoutRequested);
    window.addEventListener('lock-register', this._onLockRegister);
    // Q.1 — wire navigator.onLine into the connection-state store at app
    // start. The realtime wiring happens later when the user logs in
    // (WithRealtime.connectRealtime attaches the SignalR side).
    void import('./state/connection-state').then(({ connectionStateStore }) => {
      this._navigatorDispose = connectionStateStore.attachNavigator(window);
    });
    // Version-skew probe — see _checkVersionSkew docstring.
    void this._checkVersionSkew();
  }

  disconnectedCallback() {
    window.removeEventListener('app-update-available', this._onUpdateAvailable);
    window.removeEventListener('user-profile-set', this._onUserProfileSet);
    window.removeEventListener('shop-created', this._onShopCreated);
    window.removeEventListener('shop-selected', this._onShopSelected);
    window.removeEventListener('auth-expired', this._onAuthExpired);
    window.removeEventListener('logout-requested', this._onLogoutRequested);
    window.removeEventListener('lock-register', this._onLockRegister);
    this._navigatorDispose?.();
    this._idleHandlers?.();
    super.disconnectedCallback();
  }

  // Decides where to send a logged-in user based on how many shops they have
  // and whether they've already picked one. Called after login and whenever
  // the user-profile or active-shop changes meaningfully.
  private _routeForShopState() {
    // Phase Y.B1 — public order-tracking pages don't participate in
    // the operator routing dance. A customer scanning a QR has no
    // session, no shops, and shouldn't trigger sample-onboarding or
    // login-redirect side-effects. Leave them on /track/{token} and
    // let the tracking page handle its own anonymous fetch.
    if (window.location.pathname.includes('/track/')) return;

    // /join/{code} pages are self-contained — they handle their own
    // auth + enrol + join flow, including the post-login return.
    if (window.location.pathname.includes('/join/')) return;

    if (!this.isLoggedIn) {
      // Public routes — stay where you are; don't force a redirect.
      const publicRoutes = [
        '/welcome',
        '/login',
        '/track/',
        '/join/',
        '/privacy',
        '/terms',
        '/about',
      ];
      const isPublic = publicRoutes.some((r) =>
        window.location.pathname.includes(r)
      );
      if (!isPublic) {
        void router.navigate(resolveRouterPath('welcome'));
      }
      return;
    }

    // Logged in with a pending shop-join from a QR scan → bounce
    // straight to the join page. The page itself clears the pending
    // code once it finishes (success / already-member / dismiss).
    const pendingJoin = getPendingJoinCode();
    if (pendingJoin) {
      const joinPath = resolveRouterPath(
        `join/${encodeURIComponent(pendingJoin)}`
      );
      if (window.location.pathname !== joinPath) {
        void router.navigate(joinPath);
      }
      return;
    }

    const access = (this.userProfile?.shopAccess ?? []).filter((s) => s.active);
    const isCustomer = this.userProfile?.isCustomer ?? false;
    const hasShops = access.length > 0;
    const path = window.location.pathname;
    const onLoginRoute = path.includes('/login') || path.includes('/welcome');
    const onShopSetupRoute =
      path.includes('/shop/create') ||
      path.includes('/shop/select') ||
      path.includes('/shop/settings');

    // Customer-only: no shop access → customer home directly.
    if (isCustomer && !hasShops) {
      // ...but a customer can also choose to run a shop. Don't trap them on
      // the wallet: if they've navigated to a shop-setup route, let them
      // stay so they can create a shop (or a sample shop) from there.
      const wantsToRunAShop =
        path.includes('/shop/create') || path.includes('/shop/sample');
      if (wantsToRunAShop) return;
      if (!path.includes('/customer')) {
        void router.navigate(resolveRouterPath('customer'));
      }
      return;
    }

    // Has both customer access and shop access → mode-picker or saved preference.
    if (isCustomer && hasShops) {
      const savedMode = getUserContextMode();
      if (!savedMode) {
        // No saved preference — show the picker so they explicitly choose.
        if (!path.includes('/mode-picker')) {
          void router.navigate(resolveRouterPath('mode-picker'));
        }
        return;
      }
      if (savedMode === 'customer') {
        if (!path.includes('/customer')) {
          void router.navigate(resolveRouterPath('customer'));
        }
        return;
      }
      // savedMode === 'shop' — fall through to existing shop routing below.
    }

    if (access.length === 0) {
      // No shops yet — auto-create a sample shop so the operator lands
      // in a working system instead of an empty create-shop form.
      // Re-entry guarded: the splash render covers the in-flight state;
      // a one-shot _autoCreateAttempted flag prevents retry loops if
      // the server keeps failing (e.g. transient error). On failure the
      // user falls through to /shop/create where they can do it
      // manually.
      if (this._autoCreatingSample) return;
      if (!this._autoCreateAttempted) {
        void this._autoCreateSampleShop();
        return;
      }
      // Auto-create already tried + failed — let the user create one
      // manually. They can also retry the sample flow via /shop/sample.
      if (!path.includes('/shop/create') && !path.includes('/shop/sample')) {
        void router.navigate(resolveRouterPath('shop/create'));
      }
      return;
    }

    if (!this.activeShop) {
      // Has shops but none picked. restoreActiveShopFor auto-selects when
      // there's exactly one, so reaching here means 2+ shops.
      if (!path.includes('/shop/select')) {
        void router.navigate(resolveRouterPath('shop/select'));
      }
      return;
    }

    // Logged in with an active shop. Bounce away from the login page (the
    // redirect-after-login case AND the refresh-on-login-while-logged-in
    // case both reach here) and from any shop-setup-only route — leaving
    // /shop/settings alone because that's a real working page. Land on
    // the dashboard so the operator sees a useful starting screen.
    if (
      onLoginRoute ||
      (onShopSetupRoute && !path.includes('/shop/settings'))
    ) {
      void router.navigate(resolveRouterPath('dashboard'));
    }
  }

  firstUpdated() {
    // Synchronous setup only — initialise SDKs and route listeners. Anything that
    // mutates state (loadAndVerifyUserProfile sets `userProfile`) is deferred to a
    // microtask so it doesn't fire during the firstUpdated cycle, which Lit warns
    // about (see lit.dev/msg/change-in-update).
    FacebookAuth.initFacebookSDK(
      (profile) => void this.setUserProfile(profile)
    );
    // Google sign-in no longer loads an SDK here — it uses a full-page OAuth
    // redirect (see authentication/google-auth.ts), started from the login page.

    router.addEventListener('route-changed', () => {
      if ('startViewTransition' in document) {
        (
          document as Document & {
            startViewTransition(callback: () => void): void;
          }
        ).startViewTransition(() => this.requestUpdate());
      } else {
        this.requestUpdate();
      }
    });

    // Deferred cold-start init - see the sync-only note above; run it as a
    // fire-and-forget microtask so state mutation lands after firstUpdated.
    queueMicrotask(() => void this._initFromColdStart());
  }

  private async _initFromColdStart(): Promise<void> {
    await this.loadAndVerifyUserProfile();
    // A register locked before reload has no profile record but a lock marker.
    // Reopen straight into the lock screen rather than the login page.
    if (!this.userProfile) {
      const lock = await posDb.master.getRecord<{
        cashierName: string;
        shopLabel: string;
      }>('register-lock');
      if (lock) {
        this._lockedContext = lock.value;
        this._locked = true;
        return;
      }
    }
    if (this.userProfile) {
      // Cold-start path mirrors the fresh-login flow: gate first, then
      // restore active shop only if the gate didn't auto-select.
      const allowed = await this._runDeviceBindingGate();
      if (!allowed) return; // already cleared profile + queued flash
      if (!this.activeShop) {
        await this.restoreActiveShopFor(this.userProfile);
      }
      await this.connectRealtime();
      await this._applyDeviceContext(this.activeShop?.shopName);
      if (this.activeShop) {
        await this.realtimeJoinShop(this.activeShop.shopName);
        await this._availability.attachToShop(
          this.activeShop.shopName,
          this.realtime
        );
      }
      // The store attaches on every OTHER way a shop becomes active (login,
      // shop created, shop switched) and has to attach here too, because this
      // is the ordinary path: a device signs in once and every subsequent
      // morning is a cold start. Without it the store holds no shop, so
      // refresh() and drainNow() both early-return - the banner and the /cash
      // pill report zero queued sales while sales sit in the outbox, and a
      // sale rung during an outage never replays when the network returns.
      await this._attachPendingSalesStore();
    }
    this._routeForShopState();
  }

  // Centered splash while we provision the new user's sample shop.
  // Rendered above the router so the user doesn't briefly see /shop/create
  // before the seed completes.
  private _renderAutoCreateSplash() {
    return html`
      <main
        style="
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 18px;
        padding: 24px;
        text-align: center;
      "
      >
        <sl-spinner style="font-size: 2.5rem;"></sl-spinner>
        <h2
          style="
          margin: 0;
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 1.4rem;
          letter-spacing: -0.02em;
        "
        >
          Setting up your sample shop…
        </h2>
        <p
          style="
          margin: 0;
          max-width: 380px;
          color: var(--app-text-muted, var(--sl-color-neutral-600));
          font-size: 0.95rem;
        "
        >
          Seeding suppliers, stock items, menu, and a published catalog so you
          can poke around without configuring anything first.
        </p>
      </main>
    `;
  }

  render() {
    // Auto-create splash takes precedence over everything — once the
    // user lands on the system with zero shops we provision the sample
    // shop and the splash covers the few seconds of seeding.
    if (this._autoCreatingSample) {
      return this._renderAutoCreateSplash();
    }

    // Manual register lock takes over the whole viewport - no shell, no route,
    // no dismiss. Only a valid PIN (or the password fallback) leaves it.
    if (this._locked) {
      return html`
        <register-lock-screen
          .cashierName=${this._lockedContext.cashierName}
          .shopLabel=${this._lockedContext.shopLabel}
          @cancel-lock=${this._onCancelLock}
        ></register-lock-screen>
      `;
    }

    // Login / shop-select / shop-create pages don't get the shell — they
    // run pre-shop-selection and shouldn't show a sidebar full of
    // back-of-house nav. We pick by pathname rather than by route object
    // because the router doesn't expose a stable "current route" handle
    // for matching.
    const pathname = window.location.pathname;
    const bare =
      pathname.endsWith('/login') ||
      pathname.endsWith('/welcome') ||
      pathname.endsWith('/shop/select') ||
      pathname.endsWith('/shop/create') ||
      // Phase Y.B1 — public order-tracking page. Renders without the
      // operator shell even when an operator is logged in (a cashier
      // scanning their own QR to test). The page makes its own
      // anonymous API call and doesn't read user-profile.
      pathname.includes('/track/') ||
      // Customer-facing pages run without the shop shell.
      pathname.endsWith('/mode-picker') ||
      pathname.endsWith('/customer') ||
      pathname.includes('/join/') ||
      !this.isLoggedIn;

    // Two independent paths to "you need to reload":
    //   1. _pendingUpdate — the SW noticed a new worker waiting. Soft
    //      reload: SKIP_WAITING + controllerchange handler does the
    //      page reload, cache stays intact.
    //   2. _versionSkewServer — boot saw the live server version
    //      drifted from this client's baked-in version, and the SW
    //      didn't surface an update. Hard reset: unregister the SW
    //      and clear caches, then reload. Loses offline cache but
    //      breaks out of the stuck state.
    // The skew banner is rendered only when the soft-update banner
    // isn't already showing (the SW-driven path is preferred — it
    // doesn't blow away offline state). Skew is the fallback.
    const skewBanner =
      !this._pendingUpdate && this._versionSkewServer
        ? html`
            <sl-alert
              class="update-banner"
              variant="warning"
              open
              closable
              @sl-after-hide=${() => (this._versionSkewServer = null)}
            >
              <strong>An older cached version of the app is running.</strong>
              <div style="margin-top: 4px; font-size: 0.85rem;">
                This device shows ${__APP_VERSION__}; the server is on
                ${this._versionSkewServer}. Tap Reload to clear the cache and
                load the latest version.
              </div>
              <div style="margin-top: 8px;">
                <sl-button
                  size="small"
                  variant="warning"
                  @click=${this._hardResetForSkew}
                >
                  Reload to update
                </sl-button>
              </div>
            </sl-alert>
          `
        : nothing;

    const updateBanner = this._pendingUpdate
      ? html`
          <sl-alert
            class="update-banner"
            variant="primary"
            open
            closable
            @sl-after-hide=${() => (this._pendingUpdate = null)}
          >
            <strong>A new version is available.</strong>
            <div style="margin-top: 6px;">
              <sl-button
                size="small"
                variant="primary"
                @click=${this._reloadForUpdate}
              >
                Reload
              </sl-button>
            </div>
          </sl-alert>
        `
      : nothing;

    if (bare) {
      return html`
        <main>${router.render()}</main>
        ${updateBanner} ${skewBanner}
        <install-prompt-card></install-prompt-card>
      `;
    }

    return html`
      <app-shell .deviceMode=${this._deviceMode} @logout=${this.handleLogout}>
        ${router.render()}
      </app-shell>
      ${updateBanner} ${skewBanner}
      <install-prompt-card></install-prompt-card>
    `;
  }
}

// Lowercase slug, hyphens, no leading/trailing hyphen. Matches the
// server's shop-name regex roughly enough to produce a valid candidate;
// the server is the final authority on uniqueness + format.
function slugifyShopName(s: string): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/['"’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 18);
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
